import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { SubscriptionPlan, PlanSelection, PlansState } from "../../types/plans";

export const initialState: PlansState = {
  plans: [],
  loading: false,
  error: null,
  selectedPlan: null,
  selectedCurrency: 'USD', // Default to USD
};

const plansSlice = createSlice({
  name: "plans",
  initialState,
  reducers: {
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
      if (action.payload) {
        state.error = null;
      }
    },
    setPlans(state, action: PayloadAction<SubscriptionPlan[]>) {
      state.plans = action.payload;
      state.loading = false;
      state.error = null;
    },
    setError(state, action: PayloadAction<string>) {
      state.error = action.payload;
      state.loading = false;
    },
    selectPlan(state, action: PayloadAction<PlanSelection>) {
      state.selectedPlan = action.payload;
    },
    updateSeatCount(state, action: PayloadAction<number>) {
      if (state.selectedPlan) {
        state.selectedPlan.seatCount = action.payload;
        // Recalculate total price
        const plan = state.plans.find(p => p.slug === state.selectedPlan!.planSlug);
        if (plan) {
          const pricePerSeat = state.selectedPlan.billingCycle === 'YEARLY' 
            ? plan.pricing.yearlyPricePerSeat
            : plan.pricing.basePricePerSeat;
          state.selectedPlan.totalPrice = pricePerSeat * action.payload;
        }
      }
    },
    updateBillingCycle(state, action: PayloadAction<'MONTHLY' | 'YEARLY'>) {
      if (state.selectedPlan) {
        state.selectedPlan.billingCycle = action.payload;
        // Recalculate total price
        const plan = state.plans.find(p => p.slug === state.selectedPlan!.planSlug);
        if (plan) {
          const pricePerSeat = action.payload === 'YEARLY' 
            ? plan.pricing.yearlyPricePerSeat
            : plan.pricing.basePricePerSeat;
          state.selectedPlan.totalPrice = pricePerSeat * state.selectedPlan.seatCount;
        }
      }
    },
    clearSelection(state) {
      state.selectedPlan = null;
    },
    clearError(state) {
      state.error = null;
    },
    setCurrency(state, action: PayloadAction<'USD' | 'INR'>) {
      state.selectedCurrency = action.payload;
      // Clear selected plan when currency changes as plans might be different
      state.selectedPlan = null;
    },
  },
});

export const {
  setLoading,
  setPlans,
  setError,
  selectPlan,
  updateSeatCount,
  updateBillingCycle,
  clearSelection,
  clearError,
  setCurrency,
} = plansSlice.actions;

export default plansSlice.reducer;